/**
 * Linked Accounts Manager Component
 *
 * Full management view for AA linked accounts. Groups accounts by FIP with
 * expand/collapse, status indicators, and revoke consent functionality.
 */

import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  revokeAAConsent,
  aaQueryKeys,
  type AAStatus,
  type LinkedAccount,
} from "@/lib/aa-api";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Clock,
  MoreVertical,
  AlertTriangle,
  Loader2,
} from "lucide-react";

// ============================================================================
// Types
// ============================================================================

export interface LinkedAccountsManagerProps {
  aaStatus: AAStatus;
  className?: string;
}

type AccountHealth = "active" | "stale" | "expired";

interface FipGroup {
  fipId: string;
  accounts: LinkedAccount[];
}

// ============================================================================
// Utilities
// ============================================================================

function formatRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  if (diffInSeconds < 60) return rtf.format(-diffInSeconds, "second");
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return rtf.format(-diffInMinutes, "minute");
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return rtf.format(-diffInHours, "hour");
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return rtf.format(-diffInDays, "day");
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return rtf.format(-diffInMonths, "month");
  return rtf.format(-Math.floor(diffInMonths / 12), "year");
}

function formatFipName(fipId: string): string {
  return fipId
    .split("-")
    .map((part, index) =>
      index === 0
        ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        : part.toUpperCase()
    )
    .join(" ");
}

function deriveHealth(aaStatus: AAStatus): AccountHealth {
  if (aaStatus.consentExpiresAt && new Date(aaStatus.consentExpiresAt) < new Date()) {
    return "expired";
  }
  if (aaStatus.lastDataFetchAt) {
    const daysSinceSync =
      (Date.now() - new Date(aaStatus.lastDataFetchAt).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceSync > 7) return "stale";
  }
  return "active";
}

function groupByFip(accounts: LinkedAccount[]): FipGroup[] {
  const map = new Map<string, LinkedAccount[]>();
  for (const account of accounts) {
    const existing = map.get(account.fipId);
    if (existing) {
      existing.push(account);
    } else {
      map.set(account.fipId, [account]);
    }
  }
  return Array.from(map, ([fipId, accounts]) => ({ fipId, accounts }));
}

const healthConfig: Record<AccountHealth, { label: string; variant: "default" | "secondary" | "destructive"; dotClass: string }> = {
  active: { label: "Active", variant: "default", dotClass: "bg-green-500" },
  stale: { label: "Stale", variant: "secondary", dotClass: "bg-yellow-500" },
  expired: { label: "Expired", variant: "destructive", dotClass: "bg-red-500" },
};

// ============================================================================
// Sub-components
// ============================================================================

function FipGroupCard({
  group,
  health,
  lastSyncTime,
}: {
  group: FipGroup;
  health: AccountHealth;
  lastSyncTime: string | null;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const config = healthConfig[health];

  return (
    <div className="rounded-lg border">
      {/* Collapsed header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">{formatFipName(group.fipId)}</p>
            <p className="text-xs text-muted-foreground">
              {group.accounts.length} account{group.accounts.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastSyncTime && (
            <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              {formatRelativeTime(lastSyncTime)}
            </span>
          )}
          <span className={cn("h-2 w-2 rounded-full", config.dotClass)} />
          <Badge variant={config.variant} className="text-xs">
            {config.label}
          </Badge>
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded account rows */}
      {expanded && (
        <div className="border-t divide-y">
          {group.accounts.map((account) => (
            <div
              key={account.linkRefNumber}
              className="flex items-center justify-between px-4 py-3 pl-[4.25rem]"
            >
              <div className="flex items-center gap-2 text-sm">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <span>{account.maskedAccNumber}</span>
                <span className="text-xs text-muted-foreground capitalize">
                  {account.fiType.toLowerCase().replace(/_/g, " ")}
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                Linked {formatRelativeTime(account.linkedAt)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function LinkedAccountsManager({
  aaStatus,
  className,
}: LinkedAccountsManagerProps) {
  const [revokeDialogOpen, setRevokeDialogOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const revokeMutation = useMutation({
    mutationFn: () => {
      if (!aaStatus.consentHandle) throw new Error("No consent handle");
      return revokeAAConsent(aaStatus.consentHandle);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aaQueryKeys.all });
      setRevokeDialogOpen(false);
    },
  });

  const health = deriveHealth(aaStatus);
  const groups = groupByFip(aaStatus.linkedAccounts);

  return (
    <div className={cn("space-y-4", className)}>
      {/* Actions dropdown */}
      {aaStatus.consentHandle && (
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <MoreVertical className="h-4 w-4" />
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setRevokeDialogOpen(true)}
              >
                <AlertTriangle className="mr-2 h-4 w-4" />
                Revoke Consent
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* FIP groups */}
      {groups.map((group) => (
        <FipGroupCard
          key={group.fipId}
          group={group}
          health={health}
          lastSyncTime={aaStatus.lastDataFetchAt}
        />
      ))}

      {/* TODO: Add fetch quota display when API is available (CT004FetchRateLimiter.getFetchQuotaInfo) */}

      {/* Revoke confirmation dialog */}
      <Dialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke AA Consent</DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke your Account Aggregator consent?
              Your linked accounts will be disconnected and portfolio data will
              no longer update automatically.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRevokeDialogOpen(false)}
              disabled={revokeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => revokeMutation.mutate()}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Revoking...
                </>
              ) : (
                "Revoke Consent"
              )}
            </Button>
          </DialogFooter>
          {revokeMutation.isError && (
            <p className="text-sm text-destructive mt-2">
              Failed to revoke consent. Please try again.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LinkedAccountsManager;
